/**
 * Node de grupo/sub-flow para agrupar nodes visualmente
 * Funciona como container visual, sem handles (não participa de conexões)
 * Permite mover múltiplos nodes juntos arrastando o grupo
 * 
 * RELEASE: 1.0.0 - 2026-03-07 - Novo node de agrupamento visual
 */

import { memo, useState, useCallback } from 'react';
import { NodeProps, NodeResizer } from '@xyflow/react';
import { FlowNodeData } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { Badge } from '@/components/ui/badge';
import { Layers } from 'lucide-react';

/**
 * Componente visual do node de grupo
 * Background semi-transparente lilás com borda tracejada
 * Header editável com nome do grupo e contagem de filhos
 */
export const GroupNode = memo(function GroupNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  // Nome do grupo (editável via duplo-clique)
  const [isEditing, setIsEditing] = useState(false);
  const [groupName, setGroupName] = useState(
    (nodeData.config as any)?.groupName || 'Grupo'
  );

  // Contagem de filhos injetada pelo FlowCanvas
  const childCount = (nodeData as any).childCount || 0;

  // Callback para desagrupar (injetado pelo FlowCanvas)
  const onUngroup = (nodeData as any).onUngroup;
  // Callback para excluir grupo (injetado pelo FlowCanvas)
  const onDeleteGroup = (nodeData as any).onDeleteGroup;
  // Callback para renomear grupo
  const onRenameGroup = (nodeData as any).onRenameGroup;
  // Callback para entrar no modo de edição (adicionar nodes)
  const onEnterEditMode = (nodeData as any).onEnterEditMode;
  // Callback para duplicar grupo completo com filhos
  const onDuplicateGroup = (nodeData as any).onDuplicateGroup;
  // Indica se o grupo está em modo de edição
  const isEditingMode = (nodeData as any).isEditingMode || false;

  /**
   * Finaliza edição do nome do grupo
   * Salva o novo nome via callback
   */
  const handleFinishEditing = useCallback(() => {
    setIsEditing(false);
    onRenameGroup?.(groupName);
  }, [groupName, onRenameGroup]);

  /**
   * Ativa modo edição ao dar duplo-clique no nome
   */
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
  }, []);

  return (
    <>
      {/* Resizer para permitir redimensionamento manual do grupo */}
      <NodeResizer
        isVisible={selected}
        minWidth={250}
        minHeight={150}
        lineClassName="!border-primary/30"
        handleClassName="!w-2.5 !h-2.5 !bg-primary !border-2 !border-background !rounded-sm"
      />

      {/* Toolbar flutuante - exibe botão desagrupar em vez dos padrões */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType="group"
        isVisible={selected}
        onUngroup={onUngroup}
        onDelete={onDeleteGroup}
        onEnterEditMode={onEnterEditMode}
        onDuplicateGroup={onDuplicateGroup}
      />

      {/* Container visual do grupo */}
      <div
        className={`w-full h-full rounded-xl border-2 transition-all ${
          isEditingMode 
            ? 'border-solid border-primary bg-primary/10 animate-pulse' 
            : 'border-dashed border-primary/40 bg-primary/5'
        }`}
        style={{ minWidth: 250, minHeight: 150 }}
      >
        {/* Header do grupo */}
        <div className="flex items-center gap-2 px-3 py-2 select-none">
          <Layers className="h-4 w-4 text-primary/70" />
          
          {isEditing ? (
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onBlur={handleFinishEditing}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFinishEditing();
                if (e.key === 'Escape') {
                  setGroupName((nodeData.config as any)?.groupName || 'Grupo');
                  setIsEditing(false);
                }
              }}
              autoFocus
              className="bg-transparent border-b border-primary/50 text-sm font-semibold text-foreground outline-none px-1 max-w-[200px]"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className="text-sm font-semibold text-foreground/80 cursor-text"
              onDoubleClick={handleDoubleClick}
              title="Duplo-clique para renomear"
            >
              {groupName}
            </span>
          )}

          {/* Badge com contagem de filhos */}
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {childCount}
          </Badge>

          {/* Indicador de modo edição */}
          {isEditingMode && (
            <Badge variant="default" className="text-[10px] px-1.5 py-0 ml-auto animate-pulse">
              Editando
            </Badge>
          )}
        </div>
      </div>
    </>
  );
});
